import React, { useState, useEffect, ChangeEvent } from 'react'
import { SelectChangeEvent } from '@mui/material/Select'
import { useSelector } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { Plan } from '../../types/planTypes'
import { getPlanbyId, updatePlan } from '../../redux/plans/planActions'
import UseAlert from '../../components/common/alert'
import SelectPriority from '../../components/common/SelectPriority'
import SelectobjMaturite from '../../components/common/SelectObjM'
import SelectMission from '../../components/common/SelectMission'
import DateSelector from '../../components/common/Date'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import SelectStatut from '../../components/common/SelectStatut'
import {
  FormAside,
  FormField,
  FormFoot,
  FormGrid,
  FormLayout,
  FormPageHead,
  FormSection,
  FormShell,
} from '../../components/common/form'

export default function PlanUpdate() {
  const [priority, setPriority] = useState<number>(1)
  const [status, setStatus] = useState<string>('EnCours')
  const [ObjectifMaturite, setObjectifMaturite] = useState<string>('Level1')
  const [MissionId, setMissionId] = useState<string>('')
  const [startDate, setDateDebut] = useState<Date>(new Date())
  const [endDate, setDateFin] = useState<Date>(new Date())
  const { t } = useTranslation()
  const [plan, setPlan] = useState<Plan>({
    _id: '',
    PlanName: '',
    Description: '',
    MissionId: '',
    DateDebut: new Date(),
    DateFin: new Date(),
    Status: '',
    Priority: 0,
    ObjectifMaturite: '',
  })

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      open: false,
    })
    window.history.back()
  }
  const dispatch: AppDispatch = useAppDispatch()
  const plansList = useSelector((state: RootState) => state.plan.plan)
  const loading = useSelector((state: RootState) => state.plan.loading)
  const error = useSelector((state: RootState) => state.plan.error)
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  useEffect(() => {
    if (id) {
      dispatch(getPlanbyId(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (plansList !== null) {
      setPlan({
        _id: plansList._id,
        PlanId: plansList.PlanId,
        PlanName: plansList.PlanName,
        Description: plansList.Description,
        MissionId: plansList.MissionId,
        DateDebut: plansList.DateDebut,
        DateFin: plansList.DateFin,
        Status: plansList.Status,
        Priority: plansList.Priority,
        ObjectifMaturite: plansList.ObjectifMaturite,
      })

      setMissionId(plansList.MissionId || '')
      setPriority((plansList.Priority, 4) || 1)
      setStatus(plansList.Status)
      setObjectifMaturite(plansList.ObjectifMaturite)
      setDateDebut(plansList.DateDebut || new Date())
      setDateFin(plansList.DateFin || new Date())
    }
  }, [plansList])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setPlan((prevPlan) => ({
      ...prevPlan,
      [name]: value,
    }))
  }

  const handleDateChange = (dates: [Date, Date] | null) => {
    if (dates) {
      const [startDate, endDate] = dates

      setPlan((prevPlan) => ({
        ...prevPlan,
        DateDebut: startDate,
        DateFin: endDate,
      }))

      console.log('Selected Start Date:', startDate)
      console.log('Selected End Date:', endDate)
    }
  }

  const createStringHandler =
    (
      setStringState: React.Dispatch<React.SetStateAction<string>>,
      property: string
    ) =>
    (event: React.ChangeEvent<{ value: string }>) => {
      const { value } = event.target

      setStringState(value)
      setPlan((prevPlan) => ({
        ...prevPlan,
        [property]: value,
      }))

      console.log(value)
    }

  const createNumberHandler =
    (
      setNumberState: React.Dispatch<React.SetStateAction<number>>,
      property: string
    ) =>
    (event: ChangeEvent<{ value: number }>) => {
      const { value } = event.target

      setNumberState(value)

      setPlan((prevPlan) => ({
        ...prevPlan,
        [property]: value,
      }))

      console.log(value)
    }

  const handleMissionChange = (newMission: string) => {
    setMissionId(newMission)
    setPlan((prevPlan) => ({
      ...prevPlan,
      MissionId: newMission,
    }))
  }

  const handleObjectifMaturiteChange = createStringHandler(
    setObjectifMaturite,
    'ObjectifMaturite'
  ) as (event: SelectChangeEvent<string>) => void

  const handleStatusChange = createStringHandler(setStatus, 'Status') as (
    event: SelectChangeEvent<string>
  ) => void

  const handlePriorityChange = createNumberHandler(setPriority, 'Priority') as (
    event: React.ChangeEvent<{ value: number }> | SelectChangeEvent<number>
  ) => void

  const submitLabel = t('Update')

  const handleUpdatePlan = async () => {
    try {
      if (plan._id) {
        await dispatch(
          updatePlan({
            updatedPlan: plan,
            _id: plan._id,
          })
        )

        console.log(plan.PlanId)

        setAlert({
          open: true,
          message: t('alerts.planUpdate.success'),
          severity: 'success',
        })
      } else {
        setAlert({
          open: true,
          message: t('alerts.planUpdate.emptyFields'),
          severity: 'warning',
        })
      }
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du plan :', error.message)

      setAlert({
        open: true,
        message: t('alerts.planUpdate.error'),
        severity: 'error',
      })
    }
  }

  if (loading && !plan._id) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress sx={{ color: '#0d9b87' }} />
      </Box>
    )
  }

  if (error && !plan._id) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  return (
    <div className="page-content">
      <FormPageHead
        eyebrow={t('entityForms.plan.updateEyebrow', {
          defaultValue: 'Management · Risk treatment plans',
        })}
        title={t('Update treatment')}
        subtitle={t('entityForms.plan.updateSubtitle', {
          defaultValue:
            'Update the plan name, mission, schedule and maturity objective.',
        })}
        backLabel={t('entityForms.plan.backToList', {
          defaultValue: 'Back to risk treatment plans',
        })}
        onBack={() => navigate('/pipelines')}
      />

      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />

      <FormLayout
        main={
          <FormShell>
            <FormSection
              title={t('entityForms.plan.identityTitle', {
                defaultValue: 'Plan details',
              })}
            >
              <FormGrid cols={2}>
                <FormField label={t('plan_name')} required span>
                  <input
                    className="incyia-form-inp"
                    name="PlanName"
                    value={plan.PlanName}
                    onChange={handleInputChange}
                    required
                  />
                </FormField>
                <FormField label={t('Mission')}>
                  <SelectMission
                    value={MissionId}
                    onChange={handleMissionChange}
                  />
                </FormField>
                <FormField label={t('Description')} required span>
                  <textarea
                    className="incyia-form-inp"
                    name="Description"
                    value={plan.Description}
                    onChange={handleInputChange as any}
                    rows={4}
                    required
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection
              title={t('entityForms.plan.planningTitle', {
                defaultValue: 'Planning and objectives',
              })}
            >
              <FormGrid cols={2}>
                <FormField
                  label={t('entityForms.project.dates', {
                    defaultValue: 'Start & end dates',
                  })}
                  span
                >
                  <DateSelector
                    placeholder={t('entityForms.datePlaceholder')}
                    onDateChange={handleDateChange}
                    defaultValue={[
                      new Date(plan.DateDebut),
                      new Date(plan.DateFin),
                    ]}
                  />
                </FormField>
                <FormField label={t('Maturity objective')}>
                  <SelectobjMaturite
                    value={ObjectifMaturite}
                    onChange={handleObjectifMaturiteChange}
                  />
                </FormField>
                <FormField label={t('Status')}>
                  <SelectStatut value={status} onChange={handleStatusChange} />
                </FormField>
                <FormField label={t('Priority')}>
                  <SelectPriority
                    value={priority}
                    onChange={handlePriorityChange}
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormFoot
              note={t('actionCreate.aside.requiredMark', {
                defaultValue: '* Required',
              })}
              cancelLabel={t('Cancel')}
              submitLabel={submitLabel}
              onCancel={() => navigate('/pipelines')}
              onSubmit={handleUpdatePlan}
            />
          </FormShell>
        }
        aside={
          <FormAside
            title={t('actionCreate.aside.inThisForm', {
              defaultValue: 'In this form',
            })}
            tipTitle={t('actionCreate.aside.goodToKnow', {
              defaultValue: 'Good to know',
            })}
            tip={t('entityForms.plan.tip', {
              defaultValue:
                'Plans group risks and treatment actions under a shared maturity target.',
            })}
            steps={[
              {
                title: t('entityForms.plan.identityTitle', {
                  defaultValue: 'Plan details',
                }),
                fieldCount: 3,
              },
              {
                title: t('entityForms.plan.planningTitle', {
                  defaultValue: 'Planning and objectives',
                }),
                fieldCount: 4,
              },
            ]}
            requiredCount={2}
            submitLabel={submitLabel}
            fieldsLabel={(n) =>
              t(
                n === 1
                  ? 'actionCreate.aside.fields_one'
                  : 'actionCreate.aside.fields_other',
                { count: n, defaultValue: `${n} fields` }
              )
            }
            requiredNote={(count, submit) =>
              t('actionCreate.aside.requiredNote', {
                count,
                submit,
                defaultValue: `${count} required before you can ${submit}.`,
              })
            }
          />
        }
      />
    </div>
  )
}
